import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Page from "./Page";
import useData from "../../hooks/useData";
import useWork from "../../hooks/useWork";
import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";

const SurveyAnswerExport = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { getFromServer, executeAction } = useData();
  const { queueWork } = useWork();
  const { getTitle } = useLocalization();
  const { showMessage } = usePage();
  const email = useSelector((state) => state.authentication.email);

  const [clientId, setClientId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [surveyId, setSurveyId] = useState(null);
  const [surveyPages, setSurveyPages] = useState([]);
  const [surveySections, setSurveySections] = useState([]);

  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [completionStatus, setCompletionStatus] = useState("complete");
  const [exportFormat, setExportFormat] = useState("standard");
  const [isLoading, setIsLoading] = useState(false);

  // Initialize from route state
  useEffect(() => {
    if (location.state?.surveyId) {
      setSurveyId(location.state.surveyId);
      setClientId(location.state.clientId);
      setProjectId(location.state.projectId);
    } else {
      showMessage("Survey ID not provided");
      navigate("/survey");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Load survey pages
  useEffect(() => {
    const loadPages = async () => {
      if (!surveyId) return;
      try {
        const pages = await getFromServer({
          entityName: "SurveyPage",
          filter: { surveyId },
        });
        setSurveyPages(Array.isArray(pages) ? pages : []);
      } catch (err) {
        console.error("Error loading survey pages", err);
        setSurveyPages([]);
      }
    };

    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Load all sections for this survey
  useEffect(() => {
    const loadSections = async () => {
      if (!surveyId) return;
      try {
        const sections = await getFromServer({
          entityName: "SurveySection",
          filter: { surveyId },
        });
        setSurveySections(Array.isArray(sections) ? sections : []);
      } catch (err) {
        console.error("Error loading survey sections", err);
        setSurveySections([]);
      }
    };

    loadSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Filter out text sections (no questions to export)
  const nonTextSections = useMemo(() => {
    return surveySections.filter((section) => section.sectionType !== "text");
  }, [surveySections]);

  // Filter sections based on selected pages (memoized to avoid re-creation on every render)
  const filteredSections = useMemo(() => {
    if (selectedPages.length === 0) return [];
    return nonTextSections.filter((section) =>
      selectedPages.includes(section.surveyPageId),
    );
  }, [nonTextSections, selectedPages]);

  // Only show pages that have at least one non-text section
  const pagesWithSections = useMemo(() => {
    const pageIdsWithSections = new Set(
      nonTextSections.map((s) => s.surveyPageId),
    );
    return surveyPages.filter((page) => pageIdsWithSections.has(page.id));
  }, [surveyPages, nonTextSections]);

  // Clear selected sections when filtered sections change
  useEffect(() => {
    const validSectionIds = filteredSections.map((s) => s.id);
    setSelectedSections((prev) => {
      const next = prev.filter((id) => validSectionIds.includes(id));
      // Avoid state churn when no change
      if (
        next.length === prev.length &&
        next.every((id, i) => id === prev[i])
      ) {
        return prev;
      }
      return next;
    });
  }, [filteredSections]);

  const handlePageToggle = (pageId) => {
    setSelectedPages((prev) => {
      const index = prev.indexOf(pageId);
      if (index >= 0) {
        return prev.filter((_, i) => i !== index);
      } else {
        return [...prev, pageId];
      }
    });
  };

  const handleSectionToggle = (sectionId) => {
    setSelectedSections((prev) => {
      const index = prev.indexOf(sectionId);
      if (index >= 0) {
        return prev.filter((_, i) => i !== index);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  const handleSelectAllPages = () => {
    if (selectedPages.length === pagesWithSections.length) {
      setSelectedPages([]);
    } else {
      setSelectedPages(pagesWithSections.map((p) => p.id));
    }
  };

  const handleSelectAllSections = () => {
    if (selectedSections.length === filteredSections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(filteredSections.map((s) => s.id));
    }
  };

  const handleExport = async () => {
    if (selectedSections.length === 0) {
      showMessage("Please select at least one section");
      return;
    }

    if (!email) {
      showMessage("User email not found");
      return;
    }

    setIsLoading(true);
    try {
      const workerType =
        exportFormat === "pivot"
          ? "exportSurveyAnswersPivot"
          : exportFormat === "skuGrouped"
            ? "exportSurveyAnswersSkuGrouped"
            : exportFormat === "callWide"
              ? "exportSurveyAnswersCallWide"
              : "exportSurveyAnswers";

      await queueWork({
        workerType,
        details: {
          email,
          surveyId,
          pageIds: selectedPages,
          sectionIds: selectedSections,
          dateRange:
            startDate || endDate ? { start: startDate, end: endDate } : null,
          completionStatus,
        },
      });

      showMessage(
        exportFormat === "pivot"
          ? "Pivot export queued successfully. You will receive an email with the download link shortly."
          : exportFormat === "skuGrouped"
            ? "SKU-grouped export queued successfully. You will receive an email with the download link shortly."
            : exportFormat === "callWide"
              ? "Call-wide export queued successfully. You will receive an email with the download link shortly."
              : "Export queued successfully. You will receive an email with the download link shortly.",
      );

      // Optionally navigate back or stay on page
      // navigate(-1);
    } catch (error) {
      showMessage(error.message || "Failed to queue export");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Page
      pageName="Export Survey Answers"
      includeToolbar
      items={[
        {
          name: "survey",
          iconName: "Assessment",
        },
      ]}
      onToolItemClick={(item) => {
        if (item === "survey") {
          navigate("/survey", {
            state: { surveyId, projectId, clientId },
          });
        }
      }}
    >
      <Grid
        container
        spacing={3}
      >
        {/* Filters Section */}
        <Grid
          item
          xs={12}
        >
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600 }}
                >
                  Export Filters
                </Typography>

                {/* Date Range */}
                <Stack
                  direction="row"
                  spacing={2}
                >
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>

                {/* Completion Status */}
                <FormControl
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <InputLabel>Completion Status</InputLabel>
                  <Select
                    value={completionStatus}
                    onChange={(e) => setCompletionStatus(e.target.value)}
                    label="Completion Status"
                  >
                    <MenuItem value="complete">Complete Only</MenuItem>
                    <MenuItem value="incomplete">Incomplete Only</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 260 }}
                >
                  <InputLabel>Export Format</InputLabel>
                  <Select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    label="Export Format"
                  >
                    <MenuItem value="standard">
                      Standard (section worksheets)
                    </MenuItem>
                    <MenuItem value="pivot">
                      Pivot-friendly (single flat worksheet)
                    </MenuItem>
                    <MenuItem value="skuGrouped">
                      SKU-grouped (SKU columns across top)
                    </MenuItem>
                    <MenuItem value="callWide">
                      Call-wide (one row per call result)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Pages Selection */}
        {pagesWithSections.length > 0 && (
          <Grid
            item
            xs={12}
            md={6}
          >
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600 }}
                    >
                      Survey Pages
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleSelectAllPages}
                      variant="outlined"
                    >
                      {selectedPages.length === pagesWithSections.length
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  </Box>
                  <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                    {pagesWithSections.map((page) => (
                      <FormControlLabel
                        key={page.id}
                        control={
                          <Checkbox
                            checked={selectedPages.includes(page.id)}
                            onChange={() => handlePageToggle(page.id)}
                          />
                        }
                        label={page.name}
                      />
                    ))}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Sections Selection */}
        <Grid
          item
          xs={12}
          md={pagesWithSections.length > 0 ? 6 : 12}
        >
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600 }}
                  >
                    Survey Sections ({selectedSections.length}/
                    {filteredSections.length})
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSelectAllSections}
                    variant="outlined"
                    disabled={filteredSections.length === 0}
                  >
                    {selectedSections.length === filteredSections.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                  {filteredSections.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Select a page above to view available sections
                    </Typography>
                  ) : (
                    filteredSections.map((section) => (
                      <FormControlLabel
                        key={section.id}
                        control={
                          <Checkbox
                            checked={selectedSections.includes(section.id)}
                            onChange={() => handleSectionToggle(section.id)}
                          />
                        }
                        label={section.name}
                      />
                    ))
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid
          item
          xs={12}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <DownloadIcon />
              }
              onClick={handleExport}
              disabled={isLoading || selectedSections.length === 0}
            >
              {isLoading
                ? "Queueing Export..."
                : exportFormat === "pivot"
                  ? "Queue Pivot Export (Email Link)"
                  : exportFormat === "skuGrouped"
                    ? "Queue SKU-Grouped Export (Email Link)"
                    : exportFormat === "callWide"
                      ? "Queue Call-Wide Export (Email Link)"
                      : "Queue Export (Email Link)"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Page>
  );
};

export default SurveyAnswerExport;
